import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import {
  IconButton,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField
} from '@mui/material'
import ReplyIcon from '@mui/icons-material/Reply'
import HighlightOffSharpIcon from '@mui/icons-material/HighlightOffSharp'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import SendIcon from '@mui/icons-material/Send'
import { useSelector } from 'react-redux'

const ConversationPopup = ({ selectedApplicationId }) => {
  const [open, setOpen] = useState(false)
  const [conversations, setConversations] = useState([])
  const [error, setError] = useState(null)
  const [replyMessage, setReplyMessage] = useState('')
  const [currentReplyId, setCurrentReplyId] = useState(null)
  const [senderEmail, setSenderEmail] = useState('') // Replace with sender's email
  const popupRef = useRef(null)

  const togglePopup = () => {
    setOpen(!open)
  }

  const handleOutsideClick = event => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleOutsideClick)
    } else {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [open])

  useEffect(() => {
    setSenderEmail(conversations[0]?.emails[0]?.sender_email)
  }, [conversations])
  const convertToUSTimezone = timestamp => {
    const date = new Date(timestamp)
    const options = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }

  const getLatestTimestamp = data => {
    return data.reduce((latest, item) => {
      const currentTimestamp = new Date(item.updated_at).getTime()
      return currentTimestamp > latest ? currentTimestamp : latest
    }, 0)
  }

  const handleReplyClick = conversationId => {
    if (currentReplyId === conversationId) {
      setCurrentReplyId(null) // Toggle off the reply box
    } else {
      setCurrentReplyId(conversationId)
    }
    setReplyMessage('') // Reset the message
  }

  const saveReplyToServer = async (
    conversationId,
    senderEmail,
    receiverEmail,
    unique_message_is
  ) => {
    const replyData = {
      conversation_id: conversationId,
      unique_email_id: unique_message_is, // Unique ID for the reply
      sender_email: senderEmail,
      email_body: replyMessage
    }

    try {
      const response = await axios.post(
        'https://insureai-communicator-1074099157700.us-central1.run.app/conversations/add_email',
        replyData,
        {
          headers: {
            'X-Access-Token':
              'bjZDbAhu9GabUZZqqk64FbaXK9GcTErIeDu5sVc6Ryt2USIgbwQUmZdvUXSevxyF',
            'X-Refresh-Token':
              'f68LK2Y5Py5ErV0qz81j4wGo9iIH7S1Aw5lLos7wDUPyitYmTSQ1Lut8s9AVYYys',
            Accept: 'application/json'
          }
        }
      )
      console.log('Reply saved successfully:', response.data)
      handleSendReply(conversationId, senderEmail, receiverEmail)
    } catch (error) {
      console.error('Error saving reply:', error)
    }
  }

  const handleSendReply = (conversationId, senderEmail, receiverEmail) => {
    if (!replyMessage.trim()) return // Prevent sending empty replies

    // Simulating the reply by adding it to the corresponding conversation's emails
    const updatedConversations = conversations.map(conversation => {
      if (conversation.conversation_id === conversationId) {
        const newReply = {
          email_id: `reply_${Date.now()}`, // Unique ID for the reply
          sender_email: senderEmail, // Replace with sender's email
          receiver_email: receiverEmail, // Replace with receiver's email
          timestamp: new Date().toISOString(),
          email_body: replyMessage
        }

        return {
          ...conversation,
          emails: [newReply, ...conversation.emails] // Append the new reply to the emails array
        }
      }
      return conversation
    })

    setConversations(updatedConversations)
    setReplyMessage('')
    setCurrentReplyId(null)
  }

  useEffect(() => {
    const fetchApplicationData = async () => {
      const headers = {
        'X-Access-Token':
          'bjZDbAhu9GabUZZqqk64FbaXK9GcTErIeDu5sVc6Ryt2USIgbwQUmZdvUXSevxyF',
        'X-Refresh-Token':
          'f68LK2Y5Py5ErV0qz81j4wGo9iIH7S1Aw5lLos7wDUPyitYmTSQ1Lut8s9AVYYys',
        Accept: 'application/json'
      }

      try {
        const response = await axios.get(
          `https://insureai-communicator-1074099157700.us-central1.run.app/conversations/${selectedApplicationId}/with_emails`,
          { headers }
        )
        setConversations(response.data?.data)
        setError(null)
      } catch (error) {
        console.error('Error fetching application data:', error)
        setError('Failed to fetch data. Please try again.')
        setConversations([])
      }
    }

    if (selectedApplicationId) {
      fetchApplicationData()
    }
  }, [selectedApplicationId])

  return (
    <>
      <IconButton
        onClick={togglePopup}
        style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          backgroundColor: 'rgb(234, 88, 12)',
          color: '#fff',
          borderRadius: '50px',
          padding: '8px 12px',
          zIndex: 999
        }}
        className='email-conversation'
      >
        <MarkEmailReadOutlinedIcon />
      </IconButton>

      {open && (
        <Box
          ref={popupRef}
          style={{
            position: 'fixed',
            top: '125px',
            right: '1%',
            height: '75%',
            backgroundColor: '#fff',
            border: '2px solid rgb(234, 88, 12)',
            borderRadius: '12px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
            zIndex: 9998,
            overflow: 'hidden'
          }}
          className='history-window'
        >
          <div
            style={{
              height: '100%',
              overflowY: 'auto',
              margin: '3px 2px 0 0',
              padding: '16px'
            }}
          >
            <div className='flex justify-between'>
              <Typography variant='h6' style={{ fontWeight: 'bold' }}>
                Application Conversation History
              </Typography>
              <HighlightOffSharpIcon
                className='text-right cursor-pointer'
                onClick={togglePopup}
              />
            </div>
            <Typography
              variant='body2'
              color='textSecondary'
              style={{ marginBottom: '10px' }}
            >
              Last update on{' '}
              {convertToUSTimezone(getLatestTimestamp(conversations))}
            </Typography>
            {error && <Typography color='error'>{error}</Typography>}

            <Divider style={{ marginBottom: '0px' }} />

            <List>
              {conversations.map(conversation => (
                <div key={conversation.conversation_id} className='mail'>
                  <ListItem alignItems='flex-start'>
                    <ListItemAvatar>
                      <Avatar style={{ backgroundColor: 'rgb(234, 88, 12)' }}>
                        <EmailOutlinedIcon style={{ color: '#fff' }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant='subtitle1'
                          style={{ fontWeight: 'bold' }}
                        >
                          {conversation.subject || 'No Subject'}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant='body2'
                            color='textSecondary'
                            style={{ marginBottom: '8px' }}
                          >
                            Updated:{' '}
                            {convertToUSTimezone(conversation.updated_at)}
                          </Typography>
                          {currentReplyId === conversation.conversation_id && (
                            <Box
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                margin: '10px 0',
                                position: 'relative'
                              }}
                            >
                              <Box
                                display='flex'
                                alignItems='center'
                                marginBottom='8px'
                              >
                                <Typography
                                  variant='body2'
                                  style={{ marginRight: '8px' }}
                                >
                                  <strong>To:</strong>
                                </Typography>
                                <TextField
                                  fullWidth
                                  variant='outlined'
                                  value={senderEmail}
                                  onChange={e => setSenderEmail(e.target.value)}
                                  style={{
                                    padding: '10px',
                                    borderRadius: '8px'
                                  }}
                                />
                              </Box>
                              <TextField
                                fullWidth
                                variant='outlined'
                                value={replyMessage}
                                onChange={e => setReplyMessage(e.target.value)}
                                placeholder='Type your reply...'
                                multiline
                                rows={3} // Increase the height with multiple rows
                                style={{
                                  marginBottom: '8px',
                                  padding: '10px',
                                  borderRadius: '8px'
                                }}
                              />
                              <IconButton
                                onClick={() =>
                                  saveReplyToServer(
                                    conversation.conversation_id,
                                    conversation.emails[0]?.receiver_email,
                                    conversation.emails[0]?.sender_email,
                                    conversation.emails[0]?.unique_message_is
                                  )
                                }
                                style={{
                                  alignSelf: 'flex-end',
                                  backgroundColor: 'rgb(234, 88, 12)', // Orange background color
                                  color: '#fff' // White icon color
                                }}
                              >
                                <SendIcon />
                              </IconButton>
                            </Box>
                          )}
                          {conversation.emails.map(email => (
                            <Box
                              key={email.email_id}
                              style={{
                                backgroundColor: '#f9f9f9',
                                borderRadius: '8px',
                                padding: '8px',
                                marginBottom: '8px'
                              }}
                            >
                              <Typography variant='body2'>
                                <strong>From:</strong> {email.sender_email}
                              </Typography>
                              <Typography variant='body2'>
                                <strong>To:</strong> {email.receiver_email}
                              </Typography>
                              <Typography
                                variant='body2'
                                color='textSecondary'
                                style={{ marginBottom: '4px' }}
                              >
                                Sent: {convertToUSTimezone(email.timestamp)}
                              </Typography>
                              <Typography
                                variant='body2'
                                dangerouslySetInnerHTML={{
                                  __html: email.email_body
                                }}
                              />
                            </Box>
                          ))}
                        </>
                      }
                    />
                    <div className='flex space-x-2'>
                      <IconButton
                        onClick={() =>
                          handleReplyClick(conversation.conversation_id)
                        }
                        style={{ color: 'rgb(234, 88, 12)' }}
                      >
                        <ReplyIcon />
                      </IconButton>
                    </div>
                  </ListItem>
                  <Divider />
                </div>
              ))}
            </List>
          </div>
        </Box>
      )}
    </>
  )
}

export default ConversationPopup
